import { ExpressionSpecification, type Map as MaplibreMap } from "maplibre-gl";
import type { FeatureCollection, Geometry } from "geojson";

import { COLOR_KEYS, COLORS } from "@/constants/colors";
import { UNIQUE_ID_PROPERTY } from "@/constants/map";
import { DatasetStyle } from "@/types/dataset";
import { FeatureDataConfig } from "@/types/map";

import { generateColorRangesUniqueStops } from "../common";
import type { LayerSublayers } from "../map";
import {
  addSingleStyleMarkerLayer,
  changeColorCustomCategoryLayer,
  changeColorCustomRangeLayer,
  generateCategoryColorExpression
} from "../map";
import { addLabelLayer } from "./label";
import { addRankLabelLayer } from "./rank";
import { buildStepColorExpression } from "./stepExpression";

/** Property key that carries a POI's identity in the feature properties. */
const POI_ID_PROPERTY = "POI ID";

/**
 * Keeps the first feature per `POI ID` and drops later duplicates.
 *
 * A POI joined against multiple Grid/H3/Catchment cells repeats across rows,
 * inflating the cluster count. Features without a `POI ID` are kept
 * (no-op for non-POI datasets).
 */
export const dedupePoiFeatures = (
  features: FeatureCollection<Geometry>["features"]
): FeatureCollection<Geometry>["features"] => {
  const seen = new Set<string>();

  return features.filter((feature) => {
    const poiId = feature.properties?.[POI_ID_PROPERTY];
    if (poiId === undefined || poiId === null) return true;

    const key = String(poiId);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

/**
 * Adds a clustered GeoJSON source to the map for point data
 */
/**
 * Returns the registered collection with `unique_id` applied. Use this for
 * `setData` to preserve feature-state via `promoteId`, and to avoid the
 * expensive `source.getData()` round-trip.
 */
export const addClusterSource = (
  map: MaplibreMap | null,
  sourceId: string,
  pointsData: FeatureCollection<Geometry>
): FeatureCollection<Geometry> | null => {
  if (!map) return null;

  const parsedPointsData: FeatureCollection<Geometry> = {
    ...pointsData,
    features: pointsData.features.map((feature, index) => ({
      ...feature,
      properties: {
        ...feature.properties,
        [UNIQUE_ID_PROPERTY]: feature.properties?.[UNIQUE_ID_PROPERTY] || index
      }
    }))
  };

  map.addSource(sourceId, {
    type: "geojson",
    data: parsedPointsData,
    cluster: true,
    clusterMaxZoom: 15,
    clusterRadius: 40,
    promoteId: UNIQUE_ID_PROPERTY
  });

  return parsedPointsData;
};

/**
 * Generates the common layer configuration for cluster layers
 */
const addClusterLayers = ({
  isNewUIDatasetConfigEnabled,
  isRankingLabelEnabled,
  map,
  dataConfig,
  category,
  style,
  colorExpression,
  singleStyleColors,
  beforeId
}: {
  isNewUIDatasetConfigEnabled: boolean;
  isRankingLabelEnabled: boolean;
  map: MaplibreMap | null;
  dataConfig: Omit<FeatureDataConfig, "data"> & { id: string };
  style: DatasetStyle;
  category: string;
  /** Per-feature color expression (ordinal/quantile). Unused in simple mode. */
  colorExpression?: ExpressionSpecification;
  /** Simple mode: a single flat fill + outline applied to every point. */
  singleStyleColors?: { fill: string; outline: string };
  beforeId?: string;
}): void => {
  if (!map || !dataConfig.sublayers) return;

  // Unclustered points: in simple mode every point uses the same flat fill;
  // otherwise the color is driven by the attribute via `colorExpression`.
  const unclusteredColor: ExpressionSpecification | string = singleStyleColors
    ? singleStyleColors.fill
    : ([
        "case",
        ["has", category],
        colorExpression,
        "#74c476"
      ] as unknown as ExpressionSpecification);

  // Outline when not selected: the simple outline color, else the default white.
  const unclusteredStrokeColor = [
    "case",
    ["boolean", ["feature-state", "selected"], false],
    "#FFCF01", // Visible outline when selected
    singleStyleColors?.outline ?? "#ffffff"
  ] as ExpressionSpecification;

  // Cluster bubbles: the flat simple fill, but teal when the simple layer uses
  // an image marker (the fill color is hidden/irrelevant in image mode).
  let clusterColor = COLORS[style.colorFamily][COLOR_KEYS[5]] ?? "#11b4da";
  if (singleStyleColors) {
    clusterColor = style.singleStyleConfig?.imageUrl
      ? COLORS.TEAL[500]
      : singleStyleColors.fill;
  }

  // Add the cluster circles layer
  map.addLayer(
    {
      id: dataConfig.sublayers.circle,
      type: "circle",
      source: dataConfig.id,
      filter: ["has", "point_count"],
      layout: {
        visibility: style?.visibility || "visible"
      },
      paint: {
        "circle-color": clusterColor,
        "circle-radius": ["step", ["get", "point_count"], 20, 100, 30, 750, 40],
        "circle-opacity": style.fillOpacity ?? 0.7,
        "circle-stroke-width": 1,
        "circle-stroke-color": [
          "case",
          ["boolean", ["feature-state", "selected"], false],
          "#FFCF01", // Visible outline when selected
          "#ffffff00" // No outline when not selected
        ]
      }
    },
    beforeId
  );

  // Add the count text layer
  map.addLayer(
    {
      id: dataConfig.sublayers.count,
      type: "symbol",
      source: dataConfig.id,
      filter: ["has", "point_count"],
      layout: {
        "text-field": "{point_count_abbreviated}",
        "text-size": 12,
        "text-font": ["Open Sans Bold", "Arial Unicode MS Bold"],
        "text-offset": [0, 0.1],
        "text-anchor": "center",
        visibility: style?.visibility || "visible"
      },
      paint: {
        "text-color": "#ffffff"
      }
    },
    beforeId
  );

  // Add the unclustered points layer
  map.addLayer(
    {
      id: dataConfig.sublayers.unclustered,
      type: "circle",
      source: dataConfig.id,
      filter: ["!", ["has", "point_count"]],
      layout: {
        visibility: style?.visibility || "visible"
      },
      paint: {
        "circle-color": unclusteredColor,
        "circle-radius": [
          "case",
          ["boolean", ["feature-state", "hover"], false],
          8,
          6
        ] as ExpressionSpecification,
        "circle-opacity": style.fillOpacity ?? 0.7,
        "circle-stroke-width": 1,
        "circle-stroke-color": unclusteredStrokeColor
      }
    },
    beforeId
  );

  addLabelLayer(isNewUIDatasetConfigEnabled, map, dataConfig, style, beforeId);
  addRankLabelLayer(isRankingLabelEnabled, map, dataConfig, style, beforeId);
};

/**
 * Adds cluster visualization layers to the map with ordinal color scale
 */
export const addClusterCategoryLayers = (
  isNewUIDatasetConfigEnabled: boolean,
  isRankingLabelEnabled: boolean,
  map: MaplibreMap | null,
  dataConfig: Omit<FeatureDataConfig, "data"> & { id: string },
  style: DatasetStyle,
  category: string,
  beforeId?: string
): void => {
  if (!map || !dataConfig.sublayers) return;

  // Create ordinal color expression using match
  const categoryColorExpression = generateCategoryColorExpression(
    style.colorRanges,
    style.colorFamily,
    category,
    style.colorSteps || 10
  );

  // Use the common function with the ordinal color expression
  addClusterLayers({
    isNewUIDatasetConfigEnabled,
    isRankingLabelEnabled,
    map,
    dataConfig,
    style,
    category,
    colorExpression: categoryColorExpression,
    beforeId
  });
};

/**
 * Adds cluster visualization layers to the map with quantile color scale
 */
export const addClusterAutoRangeLayers = (
  isNewUIDatasetConfigEnabled: boolean,
  isRankingLabelEnabled: boolean,
  map: MaplibreMap | null,
  dataConfig: Omit<FeatureDataConfig, "data"> & { id: string },
  style: DatasetStyle,
  category: string,
  beforeId?: string
): void => {
  if (!map || !dataConfig.sublayers) return;

  const uniqueRanges = generateColorRangesUniqueStops(style.colorRanges);

  const stepColorExpression = buildStepColorExpression(uniqueRanges, category);

  // Use the common function with the step color expression
  addClusterLayers({
    isNewUIDatasetConfigEnabled,
    isRankingLabelEnabled,
    map,
    dataConfig,
    style,
    category,
    colorExpression: stepColorExpression,
    beforeId
  });
};

/**
 * Adds cluster visualization layers with the "Simple" color scale — a single
 * uniform fill + outline applied to every point (no per-value classification).
 * Falls back to the legacy `fillColor`/`color` if `singleStyleConfig` is absent.
 */
export const addClusterSingleStyleLayers = (
  isNewUIDatasetConfigEnabled: boolean,
  isRankingLabelEnabled: boolean,
  map: MaplibreMap | null,
  dataConfig: Omit<FeatureDataConfig, "data"> & { id: string },
  style: DatasetStyle,
  category: string,
  beforeId?: string
): void => {
  if (!map || !dataConfig.sublayers) return;

  addClusterLayers({
    isNewUIDatasetConfigEnabled,
    isRankingLabelEnabled,
    map,
    dataConfig,
    style,
    category,
    singleStyleColors: {
      fill: style.singleStyleConfig?.fillColor ?? style.fillColor,
      outline: style.singleStyleConfig?.outlineColor ?? style.color
    },
    beforeId
  });

  // Add the image-marker symbol layer (hidden unless an image or a platform
  // icon is set). When one is present it replaces the dot per PRD.
  addSingleStyleMarkerLayer(
    map,
    dataConfig.id,
    dataConfig.sublayers as LayerSublayers,
    style.fillOpacity ?? 0.7,
    style.singleStyleConfig ?? null,
    beforeId
  );
};

/**
 * Adds cluster visualization layers with the "Custom Ordinal" color scale: the
 * standard cluster/unclustered point structure, then a per-value fill + outline
 * driven by the committed config (a point's fill/stroke follow the category its
 * attribute value is assigned to; unassigned points fall back to "Other"). Keeps
 * the creation-time paint in sync with the on-save repaint, so the colors
 * survive reloads, visibility toggles and filtering (which rebuild the layer).
 */
export const addClusterCustomCategoryLayers = (
  isNewUIDatasetConfigEnabled: boolean,
  isRankingLabelEnabled: boolean,
  map: MaplibreMap | null,
  dataConfig: Omit<FeatureDataConfig, "data"> & { id: string },
  style: DatasetStyle,
  category: string,
  beforeId?: string
): void => {
  if (!map || !dataConfig.sublayers) return;

  // Flat layers first (the shared builder only paints flat), then repaint
  // per-value from the saved config via `changeColorCustomCategoryLayer` below.
  addClusterLayers({
    isNewUIDatasetConfigEnabled,
    isRankingLabelEnabled,
    map,
    dataConfig,
    style,
    category,
    singleStyleColors: {
      fill: style.fillColor,
      outline: style.color
    },
    beforeId
  });

  // Create the (initially hidden) symbol layer so per-category image markers
  // have somewhere to render — `changeColorCustomCategoryLayer` populates it.
  addSingleStyleMarkerLayer(
    map,
    dataConfig.id,
    dataConfig.sublayers as LayerSublayers,
    style.fillOpacity ?? 0.7,
    null,
    beforeId
  );

  if (style.customCategoryConfig) {
    changeColorCustomCategoryLayer(
      map,
      dataConfig.sublayers as LayerSublayers,
      style.customCategoryConfig,
      category,
      style.fillOpacity ?? 0.7
    );
  }
};

export const addClusterCustomRangeLayers = (
  isNewUIDatasetConfigEnabled: boolean,
  isRankingLabelEnabled: boolean,
  map: MaplibreMap | null,
  dataConfig: Omit<FeatureDataConfig, "data"> & { id: string },
  style: DatasetStyle,
  category: string,
  beforeId?: string
): void => {
  if (!map || !dataConfig.sublayers) return;

  // Flat layers first (the shared builder only paints flat), then repaint
  // per-range from the saved config via `changeColorCustomRangeLayer` below.
  addClusterLayers({
    isNewUIDatasetConfigEnabled,
    isRankingLabelEnabled,
    map,
    dataConfig,
    style,
    category,
    singleStyleColors: {
      fill: style.fillColor,
      outline: style.color
    },
    beforeId
  });

  // Create the (initially hidden) symbol layer so per-range image markers have
  // somewhere to render — `changeColorCustomRangeLayer` populates it.
  addSingleStyleMarkerLayer(
    map,
    dataConfig.id,
    dataConfig.sublayers as LayerSublayers,
    style.fillOpacity ?? 0.7,
    null,
    beforeId
  );

  if (style.customRangeConfig) {
    changeColorCustomRangeLayer(
      map,
      dataConfig.sublayers as LayerSublayers,
      style.customRangeConfig,
      category,
      style.fillOpacity ?? 0.7
    );
  }
};
